import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { addDays, dayRange, localDate, monthGrid, shiftMonth, startOfWeek, today } from '../../lib/dates';
import { T, useGym } from '../../lib/gym';
import { useApi } from '../../lib/hooks';
import { useI18n } from '../../lib/i18n';
import { Link, setQuery, useLocation } from '../../lib/router';
import type { ScheduleItem, TokenBalance } from '../../lib/types';
import { AsyncButton, Badge, Button, cx, Empty, ErrorBox, Loading, Modal, PageHeader, Select, useToast } from '../../ui/components';
import { groupByDay, LessonRow, ScheduleList, SpotsBadge } from '../../ui/lessons';

type View = 'month' | 'week' | 'day';

export function TokenChips({ tokens }: { tokens?: TokenBalance[] }) {
  const i18n = useI18n();
  const list = (tokens ?? []).filter((t) => t.available > 0);
  if (!tokens) return null;
  if (!list.length) return <p className="small muted">{i18n.t('tokens.none')}</p>;
  return (
    <div className="chips">
      {list.map((t) => (
        <span key={t.tokenTypeId} className="token-chip" style={{ borderColor: t.color ?? undefined }} title={t.nextExpiry ? i18n.t('tokens.expires', { date: i18n.dateTime(t.nextExpiry) }) : undefined}>
          <strong>{t.available}×</strong> {t.name}
          {t.nextExpiry && <small className="muted"> · {i18n.t('tokens.until', { date: i18n.date(t.nextExpiry, { weekday: 'short', day: 'numeric', month: 'short' }) })}</small>}
        </span>
      ))}
    </div>
  );
}

export function LessonModal({ item, onClose, onChanged }: { item: ScheduleItem; onClose(): void; onChanged(): void }) {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const now = new Date().toISOString();
  const started = item.startsAt <= now;
  const late = now > item.cancelFreeUntil;
  const origin = window.location.origin;
  const book = async (extra: Record<string, unknown> = {}): Promise<void> => {
    const r = await api(`${T(gym.slug)}/bookings`, {
      body: { sessionId: item.id, source: 'web', successUrl: `${origin}${gym.base}/bookings?paid=1`, cancelUrl: `${origin}${gym.base}/planner?payment=cancelled`, ...extra },
    });
    if (r.status === 'pending_payment' && r.checkoutUrl) { window.location.href = r.checkoutUrl; return; }
    toast.ok(i18n.t(r.status === 'waitlisted' ? 'planner.joined_waitlist' : 'planner.booked'));
    onChanged();
    onClose();
  };
  const cancel = async (): Promise<void> => {
    const r = await api(`${T(gym.slug)}/bookings/${item.myBooking!.id}/cancel`, { body: {} });
    toast.ok(i18n.t(r.late ? 'planner.cancelled_late' : r.status === 'waitlist_left' ? 'planner.left_waitlist' : 'planner.cancelled'));
    onChanged();
    onClose();
  };
  const my = item.myBooking;
  let actions = null;
  if (started) actions = <p className="muted">{i18n.t('planner.started')}</p>;
  else if (my?.status === 'booked') {
    actions = (
      <div className="stack-sm">
        <p className={cx('small', late ? 'warn' : 'muted')}>{late ? i18n.t('planner.late_warning') : i18n.t('planner.free_until', { time: i18n.dateTime(item.cancelFreeUntil) })}</p>
        <AsyncButton variant="danger" confirm={late ? i18n.t('planner.late_confirm') : undefined} onClick={cancel}>{i18n.t('planner.cancel_booking')}</AsyncButton>
      </div>
    );
  } else if (my?.status === 'waitlisted') {
    actions = <AsyncButton variant="secondary" onClick={cancel}>{i18n.t('planner.leave_waitlist')}</AsyncButton>;
  } else if (my?.status === 'pending_payment') {
    actions = <p className="muted">{i18n.t('planner.payment_pending')}</p>;
  } else {
    switch (item.bookable) {
      case 'ok': actions = <AsyncButton size="lg" onClick={() => book()}>{i18n.t('planner.book')}</AsyncButton>; break;
      case 'waitlist': actions = (
        <div className="stack-sm">
          <p className="small muted">{i18n.t('planner.waitlist_explain')}</p>
          <AsyncButton onClick={() => book({ joinWaitlist: true })}>{i18n.t('planner.join_waitlist')}</AsyncButton>
        </div>
      ); break;
      case 'upgrade': actions = (
        <div className="stack-sm">
          <p className="small muted">{i18n.t('planner.upgrade_explain', { price: i18n.money(item.upgradePriceCents) })}</p>
          <AsyncButton onClick={() => book({ payment: 'upgrade' })}>{i18n.t('planner.upgrade_book', { price: i18n.money(item.upgradePriceCents) })}</AsyncButton>
        </div>
      ); break;
      case 'pay': actions = <AsyncButton onClick={() => book({ payment: 'drop_in' })}>{i18n.t('planner.pay_book', { price: i18n.money(item.dropInPriceCents) })}</AsyncButton>; break;
      case 'no_tokens': actions = <p className="muted">{i18n.t('planner.no_tokens')} <Link to={`${gym.base}/shop`}>{i18n.t('nav.shop')}</Link></p>; break;
      case 'not_open': actions = <p className="muted">{i18n.t('planner.opens_at', { time: i18n.dateTime(item.bookingOpensAt) })}</p>; break;
      case 'full': actions = <p className="muted">{i18n.t('planner.full')}</p>; break;
      default: actions = null;
    }
  }
  return (
    <Modal title={item.lesson.name} onClose={onClose}>
      <div className="stack">
        <div className="lesson-meta">
          <div><span className="muted">{i18n.t('planner.when')}</span><strong>{i18n.date(item.startsAt, { weekday: 'long', day: 'numeric', month: 'long' })}, {i18n.time(item.startsAt)}–{i18n.time(item.endsAt)}</strong></div>
          <div><span className="muted">{i18n.t('planner.where')}</span><strong>{[item.location.name, item.room].filter(Boolean).join(' · ')}</strong></div>
          {item.trainer && <div><span className="muted">{i18n.t('planner.trainer')}</span><strong>{item.trainer.name}</strong></div>}
          <div><span className="muted">{i18n.t('planner.attendees')}</span><strong>{i18n.t('planner.booked_of', { booked: item.booked, capacity: item.capacity })}{item.waitlist ? ` · ${i18n.t('planner.waiting', { count: item.waitlist })}` : ''}</strong></div>
          {item.tokens && <div><span className="muted">{i18n.t('planner.token')}</span><strong>{item.tokens.required}× {item.tokens.name}{item.tokens.available !== null ? ` (${i18n.t('planner.you_have', { count: item.tokens.available })})` : ''}</strong></div>}
          {item.lesson.level && <div><span className="muted">{i18n.field('level')}</span><strong>{i18n.option(item.lesson.level)}</strong></div>}
        </div>
        {item.lesson.description && <p>{item.lesson.description}</p>}
        <div><SpotsBadge item={item} /></div>
        {actions}
      </div>
    </Modal>
  );
}

export function PlannerPage() {
  const gym = useGym();
  const i18n = useI18n();
  const toast = useToast();
  const { query } = useLocation();
  const tz = gym.gym.timezone;
  const ws = gym.gym.policies.weekStartsOn ?? 1;
  const view = (query.get('view') as View) || (window.innerWidth < 760 ? 'day' : 'week');
  const date = query.get('date') || today(tz);
  const [loc, setLoc] = useState('');
  const [lessonType, setLessonType] = useState('');
  const [open, setOpen] = useState<ScheduleItem | null>(null);
  const [from, to] = useMemo(() => {
    if (view === 'month') { const g = monthGrid(date, ws); return [g[0], addDays(g[41], 1)]; }
    if (view === 'week') { const s = startOfWeek(date, ws); return [s, addDays(s, 7)]; }
    return [date, addDays(date, 7)];
  }, [view, date, ws]);
  const data = useApi(() => api<ScheduleItem[]>(`${T(gym.slug)}/schedule`, { query: { ...dayRange(from, to, tz), location: loc, lessonType } }), [from, to, loc, lessonType]);
  const tokens = useApi(() => api<TokenBalance[]>(`${T(gym.slug)}/me/tokens`), []);
  useEffect(() => {
    if (query.get('payment') === 'cancelled') { toast.info(i18n.t('planner.payment_cancelled')); setQuery({ payment: null }); }
  }, []);
  const step = (dir: number): void => setQuery({ date: view === 'month' ? shiftMonth(date, dir) : addDays(date, dir * (view === 'week' ? 7 : 1)) });
  const reload = (): void => { data.reload(); tokens.reload(); };
  const title = view === 'month' ? i18n.day(date, { month: 'long', year: 'numeric' })
    : view === 'week' ? `${i18n.day(from, { day: 'numeric', month: 'short' })} – ${i18n.day(addDays(to, -1), { day: 'numeric', month: 'short' })}`
      : i18n.day(date, { weekday: 'long', day: 'numeric', month: 'long' });
  const items = data.data ?? [];
  return (
    <div className="page">
      <PageHeader title={i18n.t('nav.planner')} actions={
        <div className="segmented" role="group">
          {(['month', 'week', 'day'] as View[]).map((v) => <button key={v} type="button" className={cx(view === v && 'active')} onClick={() => setQuery({ view: v })}>{i18n.t(`planner.view_${v}`)}</button>)}
        </div>
      } />
      <TokenChips tokens={tokens.data} />
      <div className="toolbar">
        <div className="row gap-sm">
          <Button variant="secondary" size="sm" aria-label={i18n.t('common.previous')} onClick={() => step(-1)}>‹</Button>
          <Button variant="ghost" size="sm" onClick={() => setQuery({ date: null })}>{i18n.t('planner.today')}</Button>
          <Button variant="secondary" size="sm" aria-label={i18n.t('common.next')} onClick={() => step(1)}>›</Button>
          <h2 className="toolbar-title">{title}</h2>
        </div>
        <div className="row gap-sm wrap">
          {gym.locations.length > 1 && <Select value={loc} onChange={(e) => setLoc(e.target.value)} placeholder={i18n.t('planner.all_locations')} options={gym.locations.map((l) => ({ value: l.id, label: l.name }))} />}
          <Select value={lessonType} onChange={(e) => setLessonType(e.target.value)} placeholder={i18n.t('planner.all_lessons')} options={gym.lessonTypes.map((l) => ({ value: l.id, label: l.name }))} />
        </div>
      </div>
      {data.error ? <ErrorBox error={data.error} onRetry={data.reload} /> : !data.data ? <Loading /> : view === 'month' ? (
        <MonthGrid date={date} ws={ws} items={items} onPickDay={(d) => setQuery({ view: 'day', date: d })} />
      ) : view === 'week' ? (
        <div className="week">
          {Array.from({ length: 7 }, (_, i) => addDays(from, i)).map((d) => {
            const list = items.filter((it) => localDate(it.startsAt, tz) === d);
            return (
              <section key={d} className={cx('week-col', d === today(tz) && 'is-today')}>
                <button type="button" className="week-day" onClick={() => setQuery({ view: 'day', date: d })}>{i18n.day(d)}</button>
                {list.length ? list.map((it) => <LessonRow key={it.id} item={it} onClick={() => setOpen(it)} />) : <p className="muted small">—</p>}
              </section>
            );
          })}
        </div>
      ) : (
        <DayView date={date} items={items} onPick={setOpen} />
      )}
      {open && <LessonModal item={open} onClose={() => setOpen(null)} onChanged={reload} />}
    </div>
  );
}

function DayView({ date, items, onPick }: { date: string; items: ScheduleItem[]; onPick(i: ScheduleItem): void }) {
  const i18n = useI18n();
  const now = today(i18n.tz);
  const byDay = new Map(groupByDay(items, i18n.tz));
  const stripStart = addDays(date, -3) < now ? (date < now ? date : now) : addDays(date, -3);
  const dayItems = byDay.get(date) ?? [];
  const next = [...byDay.keys()].find((d) => d > date);
  return (
    <>
      <div className="day-strip">
        {Array.from({ length: 7 }, (_, i) => addDays(stripStart, i)).map((d) => (
          <button type="button" key={d} className={cx('day-chip', d === date && 'active')} onClick={() => setQuery({ date: d })}>
            <small>{i18n.day(d, { weekday: 'short' })}</small><strong>{Number(d.slice(8))}</strong>
            <span className={cx('dot', !byDay.has(d) && 'hidden')} aria-hidden="true" />
          </button>
        ))}
      </div>
      {dayItems.length ? <ScheduleList items={dayItems} onPick={onPick} /> : (
        <Empty icon="📅">
          <p>{i18n.t('planner.no_lessons_day')}</p>
          <Button variant="secondary" onClick={() => setQuery({ date: next ?? addDays(date, 7) })}>{i18n.t(next ? 'planner.next_day_with_lessons' : 'planner.next_week')}</Button>
        </Empty>
      )}
    </>
  );
}

function MonthGrid({ date, ws, items, onPickDay }: { date: string; ws: number; items: ScheduleItem[]; onPickDay(d: string): void }) {
  const i18n = useI18n();
  const byDay = new Map(groupByDay(items, i18n.tz));
  const month = date.slice(0, 7);
  const cells = monthGrid(date, ws);
  const now = today(i18n.tz);
  if (!items.length) return <Empty icon="📅">{i18n.t('planner.no_lessons')}</Empty>;
  return (
    <div className="month">
      {cells.slice(0, 7).map((d) => <div key={`h${d}`} className="month-head">{i18n.day(d, { weekday: 'short' })}</div>)}
      {cells.map((d) => {
        const list = byDay.get(d) ?? [];
        const mine = list.filter((x) => x.myBooking).length;
        return (
          <button type="button" key={d} className={cx('month-cell', d.slice(0, 7) !== month && 'other', d === now && 'is-today')} onClick={() => onPickDay(d)}>
            <span className="month-num">{Number(d.slice(8))}</span>
            {list.slice(0, 3).map((x) => <span key={x.id} className="month-item" style={{ borderLeftColor: x.lesson.color ?? 'var(--brand)' }}>{i18n.time(x.startsAt)} {x.lesson.name}</span>)}
            {list.length > 3 && <span className="small muted">+{list.length - 3}</span>}
            {mine > 0 && <Badge value="booked" label={`✓ ${mine}`} />}
          </button>
        );
      })}
    </div>
  );
}
